import { BaseModel } from "@models";
import { Operation, PagingOptions } from "@api";
import { Filterable, FilterableType, MyRingLoader } from "@shared";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { TableDefaults, ExportData, PrintComponent } from ".";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { RiFileExcel2Fill, RiFilterLine, RiFilterOffFill, RiPrinterFill } from "react-icons/ri";
import * as XLSX from "xlsx";
import ReactDOM from "react-dom";
import { createRoot, Root } from "react-dom/client";

interface Props<T extends BaseModel> {
  callback?: (options: PagingOptions) => void;
  filters?: Filterable<T>[];
  exportAsXSLS?: ExportData;
  onPrint?: PrintComponent;
  className?: string;
  color?: string;
}

export const TableVerticalConfigs = <T extends BaseModel>({
  callback,
  filters,
  exportAsXSLS,
  onPrint,
  className,
  color,
}: Props<T>) => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const [queryStrings] = useSearchParams();

  const [queryStringsExist, setQueryStringsExist] = useState(false);

  const modalRootRef = useRef<Root | null>(null);

  useEffect(() => {
    setQueryStringsExist(window.location.href.includes("?"));
  }, [queryStrings]);

  useEffect(() => {
    // const container = document.getElementById("swal-filters-container");
    // if (modalRootRef.current && container) {
    if (modalRootRef.current && Swal.isVisible()) {
      modalRootRef.current.render(<ShowVerticalFilters callback={applyFilter} fields={filters!} />);
    }
  }, [filters]);

  const showFiltersOnClickHandler = () => {
    MySwal.fire({
      showConfirmButton: false,
      allowOutsideClick: false,
      showCloseButton: true,
      width: 1200,
      // Provide a container for React to mount into.
      html: `<div id="swal-filters-container"></div>`,
      didOpen: () => {
        const container = document.getElementById("swal-filters-container");
        if (container) {
          modalRootRef.current = createRoot(container);
          modalRootRef.current.render(<ShowVerticalFilters callback={applyFilter} fields={filters!} />);
        }
      }
    });
  };

  const applyFilter = (params: {}[]) => {
    const options = new PagingOptions(TableDefaults.page, TableDefaults.size);

    params.forEach((param: any) => {
      // param.type === 'select' && Object.assign(options, options.select(s => s.select<T>(...param.fields)));
      param.type === "filter" &&
        Object.assign(
          options,
          options.filter<T>((f) => f[param.operation as Operation](param.field, param.value))
        );
      param.type === "sort" && Object.assign(options, options.sort<T>(param.field, param.ascending));
      param.type === "page" && Object.assign(options, (options.page = param.value));
      param.type === "size" && Object.assign(options, (options.size = param.value));
    });

    navigate({ search: `?${options.format()}` });
    callback?.(options);
  };

  const onResetClickHandler = () => {
    navigate({ search: `` });
  };

  const handleOnExport = async () => {
    var readyData = await exportAsXSLS?.data();
    let wb = XLSX.utils.book_new();
    let ws = XLSX.utils.json_to_sheet(readyData ?? []);
    XLSX.utils.book_append_sheet(wb, ws, `${exportAsXSLS?.sheetName ?? "Sheet 01"}`);
    XLSX.writeFile(wb, `${exportAsXSLS?.fileName}.xlsx`);
  };

  // Prints a copy of the report inside a hidden iframe, so the page doesn't have to be reloaded afterwards.
  const onPrintTbl = () => {
    var source = document.getElementById(onPrint?.ref ?? "") as HTMLElement;
    if (!source) return;

    var element = source.cloneNode(true) as HTMLElement;

    // Remove the bottom pagination if exists
    element.querySelector("#bottomPagination")?.classList.add("d-none");

    onPrint?.prePrint?.(element);

    var iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);

    var doc = iframe.contentDocument!;
    var styles = Array.from(document.querySelectorAll("link[rel='stylesheet'], style"))
      .map((s) => s.outerHTML)
      .join("");
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><base href="${document.baseURI}">${styles}</head><body>${element.innerHTML}</body></html>`);
    doc.close();

    var printed = false;
    var print = () => {
      if (printed) return;
      printed = true;
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => iframe.remove(), 1000);
    };
    iframe.onload = print;
    // Fallback in case the load event already fired or never fires.
    setTimeout(print, 1000);
  };

  return (
    <>
      {(filters ?? []).length > 0 && (
        <button
          className={`btn btn-sm btn-outline-dark rounded-circle m-1 float-right ${className}`}
          onClick={showFiltersOnClickHandler}
        >
          <RiFilterLine color={`${color || ""}`} />
        </button>
      )}
      {queryStringsExist && (
        <button className="btn btn-sm btn-outline-danger rounded-circle m-1 float-right" onClick={onResetClickHandler}>
          <RiFilterOffFill />
        </button>
      )}
      {Object.keys(exportAsXSLS ?? {}).length > 0 && (
        <button className="btn btn-sm btn-outline-success rounded-circle m-1 float-right" onClick={handleOnExport}>
          <RiFileExcel2Fill />
        </button>
      )}
      {onPrint && (
        <button className="btn btn-sm btn-outline-warning rounded-circle m-1 float-right" onClick={onPrintTbl}>
          <RiPrinterFill />
        </button>
      )}
    </>
  );
};

interface miniProps<T extends BaseModel> {
  callback: (filters: {}[]) => void;
  fields: Filterable<T>[];
}

export const ShowVerticalFilters = <T extends BaseModel>({ callback, fields }: miniProps<T>) => {
  const { register, handleSubmit } = useForm();

  const onSubmit = (data: any, e: any) => {
    e.preventDefault();

    const filters: {}[] = [];

    fields.forEach((f) => {
      const filterOperation = data[`${f.key! as string}filterOperation`];
      const filterValue = data[`${f.key! as string}filterValue`];

      if (filterValue) {
        filters.push({
          type: "filter",
          field: f.key,
          operation: filterOperation ?? f?.operation ?? Operation.eq,
          value: filterValue,
        });
      }

      // for date ranges
      let from = data[`${f.key! as string}From`];
      let to = data[`${f.key! as string}To`];

      if (from && to) {
        // from = new Date(new Date(from).setHours(0, 0, 0, 1)).toISOString();
        // to = new Date(new Date(to).setHours(23, 59, 59, 999)).toISOString();
        from = from + "T00:00:00.001Z";
        to = to + "T23:59:59.999Z";
        filters.push({
          type: "filter",
          field: f.key,
          operation: Operation.gte,
          value: from,
        });
        filters.push({
          type: "filter",
          field: f.key,
          operation: Operation.lte,
          value: to,
        });
      }

      // for NumberRanges
      let fromNumberRange = data[`${f.key! as string}StartNumberRange`];
      let toNumberRange = data[`${f.key! as string}EndNumberRange`];

      if (fromNumberRange && toNumberRange) {
        filters.push({
          type: "filter",
          field: f.key,
          operation: Operation.gte,
          value: +fromNumberRange,
        });
        filters.push({
          type: "filter",
          field: f.key,
          operation: Operation.lte,
          value: +toNumberRange,
        });
      }
    });

    data.SortField &&
      filters.push({
        type: "sort",
        field: data.SortField,
        ascending: data.sortDirection ?? true,
      });
    data.pageSize && filters.push({ type: "size", value: data.pageSize });

    callback(filters);

    e.target.reset();
    Swal.close();
  };

  const displayOperation = (o: Operation): string => {
    switch (o) {
      case Operation.eq:
        return "Equal";
      case Operation.gte:
        return "Greater than or Equal";
      case Operation.lte:
        return "Less than or Equal";
      case Operation.gt:
        return "Greater than";
      case Operation.lt:
        return "Less than";

      default:
        return "";
    }
  };

  const renderInputType = (field: string) => {
    const inputType = fields.filter((f) => f.key === field)[0].format as FilterableType;

    const selectBoxData = fields?.filter((f) => f.key === field)[0]?.data;
    const callback = fields?.filter((f) => f.key === field)[0]?.callback;

    if (inputType === "text" || inputType === "date" || inputType === "month" || inputType === "number") {
      return (
        <div className="col-5">
          <div className="form-group">
            <input
              type={inputType}
              name={`${field}filterValue`}
              ref={register()}
              onChange={(e) => callback?.(e.target.value)}
            />
          </div>
        </div>
      );
    }
    if (inputType === "select") {
      return (
        <div className="col-5">
          <div className="form-group">
            <select name={`${field}filterValue`} ref={register()} onChange={(e) => callback?.(e.target.value)}>
              <option value=""> </option>
              {selectBoxData ? (
                selectBoxData.map((f, i) => {
                  return (
                    <option key={i} value={f.id}>
                      {f.name}
                    </option>
                  );
                })
              ) : (
                <option value="">no Data</option>
              )}
            </select>
          </div>
        </div>
      );
    }
    if (inputType === "dateRange") {
      return (
        <>
          <div className="col">
            <div className="form-group">
              <input type="date" name={`${field}From`} ref={register()} onChange={(e) => callback?.(e.target.value)} />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <input type="date" name={`${field}To`} ref={register()} onChange={(e) => callback?.(e.target.value)} />
            </div>
          </div>
        </>
      );
    }
    if (inputType === "numberRange") {
      return (
        <>
          <div className="col">
            <div className="form-group">
              <input
                type="number"
                name={`${field}StartNumberRange`}
                ref={register()}
                onChange={(e) => callback?.(e.target.value)}
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <input
                type="number"
                name={`${field}EndNumberRange`}
                ref={register()}
                onChange={(e) => callback?.(e.target.value)}
              />
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Filtering</title>
        <link href="/assets/customInput.css" rel="stylesheet" type="text/css" />
      </Helmet>

      <h5>Table filtering dashboard</h5>
      <hr />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-2">
            <h4 className="font-weight-bold mt-3">Filter</h4>
          </div>
          <div className="col-10">
            {fields.map((f, i) => {
              return (
                <div className="row">
                  <div className="col-2  mt-3 text-left">
                    <h5>{f.title.capitalize()}</h5>
                  </div>
                  {f?.showOperations && (
                    <div className="col">
                      <select className="custom-select" name={`${f.key! as string}filterOperation`} ref={register()}>
                        <option value=""> </option>
                        {Object.keys(Operation).map((o, i) => {
                          return (
                            <option key={i} value={o}>
                              {displayOperation(Operation[o as unknown as Operation])}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  {renderInputType(f.key! as string)}
                </div>
              );
            })}
          </div>
        </div>

        <div className="row">
          <div className="col-4"></div>
          <div className="col">
            <hr style={{ borderTop: "1px solid #D6D8D9" }} />
          </div>
          <div className="col-1"></div>
        </div>

        <div className="row">
          <div className="col-2">
            <h4 className="font-weight-bold mt-3">Sort</h4>
          </div>
          <div className="col-10">
            <div className="row">
              <div className="col-2"></div>
              <div className="col">
                <select className="custom-select custom-select-sm" name="SortField" ref={register()}>
                  <option value=""> </option>
                  {fields.map((f, i) => {
                    return (
                      <option key={i} value={f.key! as string}>
                        {f.title}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col">
                <select className="custom-select custom-select-sm" name="sortDirection" ref={register()}>
                  <option value=""> </option>
                  <option value="true">Ascending</option>
                  <option value="false">Descending</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-4"></div>
          <div className="col">
            <hr style={{ borderTop: "1px solid #D6D8D9" }} />
          </div>
          <div className="col-1"></div>
        </div>

        <div className="row mt-3 pt-1">
          <div className="col-2">
            <h4 className="font-weight-bold mt-3">Page size</h4>
          </div>

          <div className="col-4 offset-2">
            <div className="form-group row  ">
              <div className="col-sm-10">
                <input type="number" placeholder="page size" name="pageSize" ref={register()} />
              </div>
            </div>
          </div>

          <div className="col-2 offset-2 mt-1">
            <input type="submit" id="submit" className="btn btn-dark float-right" />
          </div>
        </div>
      </form>
    </>
  );
};
